import { makeAutoObservable, runInAction } from "mobx";
import { mockRecordSpaceVisit, useLongfansMock } from "../../global/GlobalMock";
import { globalModel } from "../../global/GlobalModel";
import { SpaceApi } from "./SpaceApi";
import type {
  GetHomeDataResult,
  MembershipProduct,
  SpaceModuleConfig,
  SpaceNavItem,
  SpaceProfile,
  UserSubscriptionItem,
} from "./SpaceType";
import { normalizeIntelCommentKind } from "./SpaceType";

function mergeModulesWithSubscriptions(
  modules: SpaceModuleConfig[],
  subs: UserSubscriptionItem[] | null
): MembershipProduct[] {
  if (subs == null || subs.length === 0) {
    return modules.map((m) => ({
      ...m,
      userSubscribed: false,
      daysRemaining: null,
    }));
  }
  const byId = new Map(subs.map((s) => [s.spaceProductId, s]));
  return modules.map((m) => {
    const s = byId.get(m.spaceProductId);
    return {
      ...m,
      userSubscribed: s?.userSubscribed ?? false,
      daysRemaining: s?.daysRemaining ?? null,
      subscribedStrategyCount: s?.subscribedStrategyCount ?? null,
      strategyEntitlements: s?.strategyEntitlements ?? null,
    };
  });
}

const sortNavKey = (a: MembershipProduct, b: MembershipProduct): number => {
  const oa = a.pcEntrance?.sortOrder;
  const ob = b.pcEntrance?.sortOrder;
  const fa = oa == null ? 999 : oa;
  const fb = ob == null ? 999 : ob;
  return fa - fb;
};

/** 我的笔记：固定导航，登录后展示，不依赖产品权益 */
export const MY_NOTES_ROUTE_KEY = "my_notes";

function buildSpacePcNavList(config: MembershipProduct[]): SpaceNavItem[] {
  const home: SpaceNavItem = {
    routeKey: "home",
    productKind: "space_home",
    label: "空间首页",
  };
  const rest: SpaceNavItem[] = [...config]
    .filter((c) => c.pcEntrance?.enabled && c.productKind !== "product_bundle")
    .sort(sortNavKey)
    .map((c) => ({
      /** 同一类型的多个产品实例必须用唯一 key，否则路由与菜单都会冲突 */
      routeKey: c.spaceProductId,
      productKind: c.productKind,
      spaceProductId: c.spaceProductId,
      label: c.displayName,
    }));
  const myNotes: SpaceNavItem = {
    routeKey: MY_NOTES_ROUTE_KEY,
    productKind: "user_notes",
    label: "我的笔记",
  };
  const items: SpaceNavItem[] = [home, ...rest];
  if (globalModel.isLoggedIn) {
    items.push(myNotes);
  }
  return items;
}

/**
 * 将地址栏 segment 解析为导航项：
 * - 新版：segment 为 spaceProductId（与 routeKey 一致）
 * - 兼容旧版：segment 为 productKind（如 group_companion），若有多个同类实例优先选已订阅项
 */
export function resolveNavItemForSection(
  section: string | undefined,
  navItems: SpaceNavItem[],
  membershipProducts: MembershipProduct[]
): SpaceNavItem | null {
  if (!section) {
    return null;
  }
  const direct = navItems.find((n) => n.routeKey === section);
  if (direct) {
    return direct;
  }
  const kindMatches = navItems.filter((n) => n.productKind === section);
  if (kindMatches.length === 0) {
    return null;
  }
  if (kindMatches.length === 1) {
    return kindMatches[0];
  }
  const subscribedFirst = kindMatches.find((n) => {
    const m = membershipProducts.find((x) => x.spaceProductId === n.spaceProductId);
    return m?.userSubscribed;
  });
  return subscribedFirst ?? kindMatches[0];
}

/** 供 SpacePage 校验路由 segment 是否在导航中存在 */
export function isValidSection(
  section: string | undefined,
  navItems: SpaceNavItem[]
): boolean {
  if (!section) {
    return false;
  }
  return (
    navItems.some((n) => n.routeKey === section) ||
    navItems.some((n) => n.productKind === section)
  );
}

function normalizeStartupModule(raw: SpaceModuleConfig): SpaceModuleConfig {
  const isGift = raw.pricingMode?.trim().toLowerCase() === "gift";
  const giftNames = Array.isArray(raw.giftTriggerDisplayNames)
    ? raw.giftTriggerDisplayNames
        .map((s) => (typeof s === "string" ? s.trim() : ""))
        .filter((s) => s !== "")
    : undefined;
  const giftMode =
    typeof raw.giftMemberMatchMode === "string" && raw.giftMemberMatchMode.trim() !== ""
      ? raw.giftMemberMatchMode.trim().toLowerCase()
      : undefined;
  return {
    ...raw,
    pricingMode: raw.pricingMode?.trim() ? raw.pricingMode : "retail",
    priceSpecs: Array.isArray(raw.priceSpecs) ? raw.priceSpecs : [],
    agreementMode: raw.agreementMode?.trim() ? raw.agreementMode : "none",
    agreementTemplateId: raw.agreementTemplateId?.trim()
      ? raw.agreementTemplateId
      : "",
    giftTriggerDisplayNames:
      isGift && giftNames && giftNames.length > 0 ? giftNames : undefined,
    giftMemberMatchMode: isGift ? giftMode : undefined,
    intelCommentKind: normalizeIntelCommentKind(raw.intelCommentKind),
  };
}

export class SpaceModel {
  spaceId = "";
  spaceModules: SpaceModuleConfig[] = [];
  membershipProducts: MembershipProduct[] = [];
  navItems: SpaceNavItem[] = [];
  configLoadError: string | null = null;
  loaded = false;

  spaceProfile: SpaceProfile | null = null;
  homeData: GetHomeDataResult | null = null;

  favorStatus: 0 | 1 = 0;

  constructor() {
    makeAutoObservable(this);
  }

  setSpaceId(id: string) {
    this.spaceId = id;
  }

  resetFavorState() {
    this.favorStatus = 0;
  }

  async loadFavorState() {
    const id = this.spaceId;
    if (!id || !globalModel.isLoggedIn) {
      runInAction(() => {
        this.favorStatus = 0;
      });
      return;
    }
    const s = await SpaceApi.getSpaceFavorState(id);
    runInAction(() => {
      this.favorStatus = s?.favorStatus === 1 ? 1 : 0;
    });
  }

  async refreshPublicProfile() {
    const id = this.spaceId;
    if (!id) {
      return;
    }
    const profileRes = await SpaceApi.getSpaceProfile(id);
    runInAction(() => {
      if (profileRes && typeof profileRes !== "string") {
        this.spaceProfile = profileRes;
      }
    });
  }

  async submitFavor(want: boolean): Promise<boolean> {
    const id = this.spaceId;
    if (!id) {
      return false;
    }
    const ok = await SpaceApi.setSpaceFavor(id, want);
    if (ok) {
      await this.loadFavorState();
      await this.refreshPublicProfile();
    }
    return ok;
  }

  async refreshSubscriptionMerge() {
    const id = this.spaceId;
    if (!id || this.spaceModules.length === 0) {
      return;
    }
    if (!globalModel.isLoggedIn) {
      runInAction(() => {
        this.membershipProducts = mergeModulesWithSubscriptions(
          this.spaceModules,
          null
        );
        this.navItems = buildSpacePcNavList(this.membershipProducts);
      });
      return;
    }
    const subRes = await SpaceApi.getSubscriptionDetail(id);
    runInAction(() => {
      if (typeof subRes === "string") {
        this.membershipProducts = mergeModulesWithSubscriptions(
          this.spaceModules,
          null
        );
      } else {
        this.membershipProducts = mergeModulesWithSubscriptions(
          this.spaceModules,
          subRes
        );
      }
      this.navItems = buildSpacePcNavList(this.membershipProducts);
    });
  }

  async loadConfig() {
    this.configLoadError = null;
    const id = this.spaceId;
    if (!id) {
      return;
    }
    runInAction(() => {
      this.loaded = false;
      this.spaceModules = [];
      this.membershipProducts = [];
      this.navItems = buildSpacePcNavList([]);
    });

    const [cfgRes, profileRes, homeRes] = await Promise.all([
      SpaceApi.getStartupConfig(id),
      SpaceApi.getSpaceProfile(id),
      SpaceApi.getSpaceHomeData(id),
    ]);

    if (typeof cfgRes === "string") {
      runInAction(() => {
        this.spaceModules = [];
        this.membershipProducts = [];
        this.navItems = buildSpacePcNavList([]);
        this.configLoadError = cfgRes;
        this.spaceProfile = profileRes;
        this.homeData = homeRes;
        this.loaded = true;
      });
      if (useLongfansMock && globalModel.isLoggedIn) {
        mockRecordSpaceVisit(id);
      }
      if (globalModel.isLoggedIn) {
        void globalModel.loadRecentSpaces();
      }
      return;
    }

    const modules = cfgRes.map(normalizeStartupModule);
    runInAction(() => {
      this.spaceModules = modules;
      this.configLoadError = null;
      this.spaceProfile = profileRes;
      this.homeData = homeRes;
      this.loaded = true;
    });
    if (useLongfansMock && globalModel.isLoggedIn) {
      mockRecordSpaceVisit(id);
    }
    if (globalModel.isLoggedIn) {
      void globalModel.loadRecentSpaces();
    }
  }
}
